import { useMemo, useState } from "react";
import { BrainCircuit, Clock, Sparkles, Trash2, Settings2, Plus } from "lucide-react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { MemorySkeleton } from "@/components/PageSkeletons";
import { toast } from "sonner";

const KIND_TABS = [
  { key: "short_term", label: "Short-term", icon: Clock, hint: "Session context, cleared on demand." },
  { key: "long_term", label: "Long-term", icon: BrainCircuit, hint: "Facts recalled across threads." },
  { key: "preference", label: "Preferences", icon: Sparkles, hint: "Your operating preferences and voice rules." },
];

function KindPill({ kind }) {
  return (
    <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-hm-border text-hm-fg-muted">
      {kind.replace("_", " ")}
    </span>
  );
}

function EntryList({ kind, entries, onForget, emptyHint }) {
  if (entries === null) {
    return (
      <div className="mono text-[11px] text-hm-fg-subtle p-4" data-testid={`memory-loading-${kind}`}>
        loading…
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div
        className="hm-surface p-8 text-center"
        data-testid={`memory-empty-${kind}`}
      >
        <BrainCircuit className="h-6 w-6 text-hm-fg-subtle mx-auto mb-2" />
        <div className="text-sm text-hm-fg-muted">{emptyHint}</div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={`memory-list-${kind}`}>
      {entries.map((m) => (
        <div
          key={m.id}
          className="hm-surface p-3 flex items-start gap-3"
          data-testid={`memory-item-${m.id}`}
        >
          <KindPill kind={m.kind} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-hm-fg-muted leading-relaxed">{m.content}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-1">
              {new Date(m.created_at).toLocaleString()} · recalled {m.recall_count}×
            </div>
          </div>
          <button
            onClick={() => onForget(m.id)}
            data-testid={`memory-forget-${m.id}`}
            className="p-1.5 rounded-md text-hm-fg-subtle hover:text-hm-danger hover:bg-hm-bg-1"
            title="Forget"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function PreferenceAdder({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const content = text.trim();
    if (!content) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/memory/preferences`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Preference remembered");
      setText("");
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="memory-add-preference"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1 border border-dashed border-hm-border"
      >
        <Plus className="h-3.5 w-3.5" />
        Teach Hail Mary a preference
      </button>
    );
  }

  return (
    <div className="hm-surface p-3 flex items-start gap-2">
      <Settings2 className="h-4 w-4 text-hm-accent mt-1.5 shrink-0" />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='e.g. "Always end briefs with a Beyond Activ CTA."'
        data-testid="memory-preference-input"
        rows={2}
        className="flex-1 px-3 py-2 rounded-md bg-hm-bg-1 border border-hm-border text-sm text-hm-fg leading-relaxed focus:outline-none focus:border-hm-accent-dim resize-none"
      />
      <div className="flex flex-col gap-1">
        <button
          onClick={submit}
          disabled={busy || !text.trim()}
          data-testid="memory-preference-save"
          className="px-3 h-8 rounded-md bg-hm-accent text-hm-bg text-xs font-medium disabled:opacity-40"
        >
          {busy ? "…" : "Save"}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            setText("");
          }}
          className="px-3 h-8 rounded-md text-xs text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function MemoryPage() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState("short_term");
  const queryClient = useQueryClient();

  // 4-way parallel fetch via useQueries. Each fetches its own cache key so
  // the sidebar prefetch primes them in advance.
  const results = useQueries({
    queries: [
      { queryKey: qk.memoryStats(), queryFn: fetchers.memoryStats },
      { queryKey: [...qk.memoryEntries("short_term"), q],  queryFn: () => fetchers.memoryEntries("short_term")  },
      { queryKey: [...qk.memoryEntries("long_term"),  q],  queryFn: () => fetchers.memoryEntries("long_term")   },
      { queryKey: [...qk.memoryEntries("preference"), q],  queryFn: () => fetchers.memoryEntries("preference")  },
    ],
  });
  const [statsQ, shortQ, longQ, prefQ] = results;
  const stats = statsQ.data;
  const buckets = {
    short_term: shortQ.data || null,
    long_term:  longQ.data  || null,
    preference: prefQ.data  || null,
  };
  const isPending = results.some((r) => r.isPending);

  const load = () => {
    queryClient.invalidateQueries({ queryKey: ["memory"] });
  };

  const forget = async (id) => {
    await fetch(`${API}/memory/entries/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    load();
  };

  const counts = useMemo(
    () => ({
      short_term: stats?.short_term ?? buckets.short_term?.length ?? 0,
      long_term: stats?.long_term ?? buckets.long_term?.length ?? 0,
      preference: stats?.preference ?? buckets.preference?.length ?? 0,
    }),
    [buckets, stats],
  );

  const activeTab = KIND_TABS.find((t) => t.key === active);

  return (
    <div data-testid="memory-page">
      <PageHeader
        title="Memory"
        badge="Live"
        subtitle="What Hail Mary remembers about you, your work, and past briefs."
      />
      {isPending && !stats ? (
        <MemorySkeleton />
      ) : stats && (
        <div className="grid grid-cols-4 gap-3 mb-6" data-testid="memory-stats">
          {["short_term", "long_term", "preference", "total"].map((k) => (
            <div key={k} className="hm-surface p-4" data-testid={`memory-stat-${k}`}>
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
                {k.replace("_", " ")}
              </div>
              <div className="mono text-2xl text-hm-fg mt-1">{stats[k]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="hm-surface p-3 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search memory across all kinds…"
          data-testid="memory-search"
          className="w-full h-8 px-3 rounded-md bg-hm-bg-1 border border-hm-border text-xs text-hm-fg placeholder:text-hm-fg-subtle focus:outline-none focus:border-hm-accent-dim"
        />
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-hm-border" data-testid="memory-tabs">
        {KIND_TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`memory-tab-${t.key}`}
              className={
                "-mb-px flex items-center gap-2 px-4 h-10 text-sm border-b-2 transition-colors " +
                (isActive
                  ? "border-hm-accent text-hm-fg"
                  : "border-transparent text-hm-fg-muted hover:text-hm-fg")
              }
            >
              <Icon className="h-4 w-4" />
              <span>{t.label}</span>
              <span className="mono text-[10px] px-1.5 py-0.5 rounded-full bg-hm-bg-1 text-hm-fg-subtle">
                {counts[t.key]}
              </span>
            </button>
          );
        })}
      </div>

      {activeTab && (
        <div className="mb-3 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
          {activeTab.hint}
        </div>
      )}

      {active === "preference" && (
        <div className="mb-3">
          <PreferenceAdder onCreated={load} />
        </div>
      )}

      <EntryList
        kind={active}
        entries={buckets[active]}
        onForget={forget}
        emptyHint={
          active === "short_term"
            ? "No session context yet. Start a chat to seed short-term memory."
            : active === "long_term"
              ? "Nothing remembered long-term yet. Recalled facts will pin here."
              : "No preferences taught. Teach Hail Mary how you write."
        }
      />
    </div>
  );
}
